import {
    Button, HorizontalSpacer, MainPageAccessor, Panel, SlideoutDecorator, SlideoutDecoratorBehavior
} from "@mcleod/components";
import { Alignment, HorizontalAlignment } from "@mcleod/core";
import { VirtualKeyboardAccessor } from "../../../../common";

const BOTTOM_SHEET_CORNER_RADIUS = 20;

export class AppSlideoutDecoratorBehavior extends SlideoutDecoratorBehavior {
    private static readonly SWIPE_THRESHOLD = 50;

    protected constructor() {
        super();
    }

    public override addCloseButton(decorator: SlideoutDecorator, closeButton: Button): void {
        closeButton.setProps({ imageWidth: 24, imageHeight: 24, rowBreak: false });
        decorator.panelHeader.insert(closeButton, 0);
    }

    public performSlideIn(decorator: SlideoutDecorator): Promise<any> {
        decorator.fillHorizontalSpace = true;
        decorator.setProps({ paddingBottom: 0 });

        const isBottomSheet = decorator.transitionOptions?.direction === Alignment.BOTTOM;
        const useHeaderHeight = decorator.fillVerticalSpace == true;
        decorator.panelHeader.setProps({
            paddingRight: 8,
            height: useHeaderHeight ? MainPageAccessor.pageHeaderHeight : 'auto'
        });

        if (useHeaderHeight) {
            // Technically, this is an example of the non-existent VerticalSpacer...
            // Need to push header information below the OS system tools (time, battery, wifi, etc.)
            decorator.panelHeader.insert(
                new HorizontalSpacer({ fillHeight: true, fillRow: false, rowBreak: true }), 0
            );
            this.padLayoutForBottomSafeArea(decorator);
        }

        // Only a content-sized sheet gets the sheet treatment.  A full-height slideout that happens to rise from
        // the bottom (the login screen, for example) is a page, not a sheet: its layout owns the bottom edge, and
        // padding the decorator instead paints a light band under a dark layout.
        if (isBottomSheet && !useHeaderHeight)
            this.styleAsBottomSheet(decorator);

        decorator.overlayProps = { ...decorator.overlayProps, coverPageHeader: true };
        decorator.transitionOptions = { ...decorator.transitionOptions, paddingBottom: 0 };

        this.addSwipeToDismissGesture(decorator);

        return super.performSlideIn(decorator);
    }

    /**
     * A full-height slideout runs under the Android navigation bar / iOS home indicator.  Pad the *layout* (not the
     * decorator) by the bottom inset so its last row can scroll clear of the buttons while its own background keeps
     * painting all the way down.
     */
    private padLayoutForBottomSafeArea(decorator: SlideoutDecorator): void {
        const layout = decorator.layout;
        if (layout?._element == null)
            return;
        const existing = layout.paddingBottom ?? 24;
        const existingCss = typeof existing === "number" ? `${existing}px` : String(existing);
        layout._element.style.paddingBottom =
            `calc(${existingCss} + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))`;
    }

    /**
     * Gives a slideout that rises from the bottom the affordances users expect from a native sheet: rounded top
     * corners, a drag handle that advertises swipe-to-dismiss, and padding so the content clears the home indicator
     * / gesture bar.  Callers can still override the radius through their own decorator props.
     */
    private styleAsBottomSheet(decorator: SlideoutDecorator): void {
        if (decorator.borderTopLeftRadius == null && decorator.borderTopRightRadius == null) {
            decorator.setProps({
                borderTopLeftRadius: BOTTOM_SHEET_CORNER_RADIUS,
                borderTopRightRadius: BOTTOM_SHEET_CORNER_RADIUS
            });
            decorator.panelHeader.setProps({
                borderTopLeftRadius: BOTTOM_SHEET_CORNER_RADIUS,
                borderTopRightRadius: BOTTOM_SHEET_CORNER_RADIUS
            });
        }
        decorator._element.style.overflow = "hidden";
        decorator._element.style.paddingBottom = "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))";

        if (decorator.overlayProps?.closeOnClickOff === true) {
            const grabber = Panel.createNoMarginNoPaddingPanel({
                id: "sodGrabber",
                fillRow: true,
                rowBreak: true,
                align: HorizontalAlignment.CENTER,
                paddingTop: 8,
                paddingBottom: 2
            });
            grabber.add(new Panel({
                width: 36,
                height: 4,
                margin: 0,
                padding: 0,
                borderRadius: 2,
                backgroundColor: "subtle.light"
            }));
            decorator.panelHeader.insert(grabber, 0);
        }
    }

    private addSwipeToDismissGesture(decorator: SlideoutDecorator): void {
        if (decorator.overlayProps?.closeOnClickOff !== true) {
            return;
        }

        const direction = decorator.transitionOptions?.direction ?? Alignment.RIGHT;
        const isHorizontal = direction === Alignment.LEFT || direction === Alignment.RIGHT;
        const isPositive = direction === Alignment.RIGHT || direction === Alignment.BOTTOM;

        const element = decorator._element;
        let startX = 0;
        let startY = 0;
        let tracking = false;

        element.addEventListener("touchstart", (event: TouchEvent) => {
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            tracking = false;
            element.style.transition = "none";
        }, { passive: true });

        element.addEventListener("touchmove", (event: TouchEvent) => {
            const currentX = event.touches[0].clientX;
            const currentY = event.touches[0].clientY;
            const diffX = currentX - startX;
            const diffY = currentY - startY;

            if (!tracking) {
                const primaryDelta = isHorizontal ? Math.abs(diffX) : Math.abs(diffY);
                const crossDelta = isHorizontal ? Math.abs(diffY) : Math.abs(diffX);
                if (primaryDelta < 10) return;
                if (crossDelta > primaryDelta) return;
                tracking = true;
            }

            const delta = isHorizontal ? diffX : diffY;
            const clampedDelta = isPositive ? Math.max(0, delta) : Math.min(0, delta);

            if (isHorizontal) {
                element.style.transform = `translateX(${clampedDelta}px)`;
            } else {
                element.style.transform = `translateY(${clampedDelta}px)`;
            }
        }, { passive: true });

        element.addEventListener("touchend", (event: TouchEvent) => {
            if (!tracking) {
                element.style.transition = "";
                element.style.transform = "";
                return;
            }

            const endX = event.changedTouches[0].clientX;
            const endY = event.changedTouches[0].clientY;
            const delta = isHorizontal ? endX - startX : endY - startY;
            const absDelta = Math.abs(delta);
            const dismissDirection = isPositive ? delta > 0 : delta < 0;

            if (dismissDirection && absDelta > AppSlideoutDecoratorBehavior.SWIPE_THRESHOLD) {
                const offscreen = isPositive
                    ? (isHorizontal ? element.offsetWidth : element.offsetHeight)
                    : -(isHorizontal ? element.offsetWidth : element.offsetHeight);
                const translate = isHorizontal ? `translateX(${offscreen}px)` : `translateY(${offscreen}px)`;
                element.style.transition = "transform 200ms ease-out";
                element.style.transform = translate;
                element.addEventListener("transitionend", () => decorator.onClose(true), { once: true });
                VirtualKeyboardAccessor.instance?.hide();
            } else {
                element.style.transition = "transform 200ms ease-out";
                element.style.transform = "";
                element.addEventListener("transitionend", () => {
                    element.style.transition = "";
                }, { once: true });
            }

            tracking = false;
        }, { passive: true });
    }
}

AppSlideoutDecoratorBehavior.register();
